import {
    BaseQueryApi,
    BaseQueryFn,
    FetchArgs,
    FetchBaseQueryError,
    FetchBaseQueryMeta,
    QueryReturnValue,
} from '@reduxjs/toolkit/query';
import {progressSet} from 'components/customer/ProductUpload/store/productUploadSlice';
import {AppApi} from 'store/customer/api';

interface CabinetSpecs {
    cabinet_type: number;
    height: string;
    width: string;
    depth?: string;
    edges?: {
        top: string;
        bottom: string;
        left: string;
        right: string;
    };
    quantity: string;
}

interface ErrorResponse {
    warnings: string[];
    errors: string[];
}

// a side comes back either as the resolved label or as the whole edge finish
// record, depending on how the backend resolved what the sheet supplied
export type RowEdgeFinish = string | {name?: string; [key: string]: unknown};

export interface RowEdgeFinishes {
    top: RowEdgeFinish;
    bottom: RowEdgeFinish;
    left: RowEdgeFinish;
    right: RowEdgeFinish;
}

export interface ProductOptions {
    cabinetType: number;
    extMaterial: number;
    extEdge: number;
    carcMaterial: number;
    carcEdge: number;
    doorStyle: number;
    edgeFinishes: RowEdgeFinishes | null;
}

export interface ValidateImportsResponse {
    cabinet_type: ErrorResponse;
    edges: ErrorResponse;
    height: ErrorResponse;
    // absent from some responses, so never assume it is there
    depth?: ErrorResponse;
    quantity: ErrorResponse;
    width: ErrorResponse;
    productOptions?: ProductOptions;
}

interface MutationArgs {
    roomId: string;
    cabinets: CabinetSpecs[];
}

// long enough for the finished bar to register, short enough not to be a wait
const COMPLETED_PROGRESS_DELAY = 500;

const noMessages = () => ({warnings: [] as string[], errors: [] as string[]});

// stands in for a chunk that never reached the backend, so the response stays
// aligned to the cabinets that were sent and the caller can tell which ones
// did not make it
const unreachedRow = (): ValidateImportsResponse => ({
    cabinet_type: {
        warnings: [],
        errors: ['could not be added. Please try again.'],
    },
    edges: noMessages(),
    height: noMessages(),
    depth: noMessages(),
    quantity: noMessages(),
    width: noMessages(),
});

const createSequentialChunkMutation = (
    method: 'POST' | 'PUT',
    chunkSize: number,
    // when a chunk fails the cabinets it carried are reported as failed rows
    // instead of failing the whole call, so partial imports are not lost
    reportPartialResults = false
) => {
    return async (
        args: MutationArgs,
        queryApi: BaseQueryApi,
        _extraOptions: unknown,
        baseQuery: BaseQueryFn<string | FetchArgs, unknown, unknown>
    ): Promise<
        QueryReturnValue<
            ValidateImportsResponse[],
            FetchBaseQueryError,
            FetchBaseQueryMeta | undefined
        >
    > => {
        const {roomId, cabinets} = args;
        const chunks: CabinetSpecs[][] = [];

        for (let i = 0; i < cabinets.length; i += chunkSize) {
            chunks.push(cabinets.slice(i, i + chunkSize));
        }

        const allResultsData: ValidateImportsResponse[] = [];
        const allErrors: FetchBaseQueryError[] = [];

        let processedCabinets = 0;
        // start progress
        queryApi.dispatch(progressSet(0));

        for (const batchOfCabinets of chunks) {
            const intervalId = setInterval(() => {
                const state = queryApi.getState() as {
                    productUpload: {progress: number};
                };
                const currentProgress = state?.productUpload?.progress ?? 0;
                const fakeProgress = currentProgress + 1;

                const nextMark = Math.round(
                    100 *
                        ((processedCabinets + batchOfCabinets.length) /
                            cabinets.length)
                );
                if (fakeProgress < nextMark) {
                    // progress smoothly
                    queryApi.dispatch(progressSet(Math.round(fakeProgress)));
                }
            }, 600);

            const result = await baseQuery(
                {
                    url: `room/${roomId}/jobcabinet/import/json`,
                    method,
                    body: batchOfCabinets,
                },
                queryApi,
                {}
            );

            if (result.error) {
                allErrors.push(result?.error);
                if (reportPartialResults) {
                    allResultsData.push(
                        ...batchOfCabinets.map(() => unreachedRow())
                    );
                }
            } else {
                const responseBody = result.data as {
                    data: ValidateImportsResponse[];
                };
                allResultsData.push(...responseBody.data);
            }

            clearInterval(intervalId);

            processedCabinets += batchOfCabinets.length;
            const realProgress = 100 * (processedCabinets / cabinets.length);
            // update marked progress
            queryApi.dispatch(progressSet(Math.round(realProgress)));

            // bit of delay between api requests, nothing follows the last
            // chunk so there is no need to wait on it
            if (processedCabinets < cabinets.length) {
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
        }

        // let the completed bar sit on screen before it is torn down
        await new Promise((resolve) =>
            setTimeout(resolve, COMPLETED_PROGRESS_DELAY)
        );

        // reset progress whether or not every chunk got through
        queryApi.dispatch(progressSet(null));

        // nothing landed, so there is no partial result worth reporting
        const everyChunkFailed = allErrors.length === chunks.length;
        if (
            allErrors.length > 0 &&
            (!reportPartialResults || everyChunkFailed)
        ) {
            return {
                error: {
                    status: 'CUSTOM_ERROR',
                    error: 'One or more import batches failed.',
                    data: allErrors,
                },
            };
        }

        return {data: allResultsData};
    };
};

const ProductUploadApi = AppApi.injectEndpoints({
    endpoints: (builder) => ({
        validateImports: builder.mutation<
            ValidateImportsResponse[],
            {roomId: string; cabinets: CabinetSpecs[]}
        >({
            queryFn: createSequentialChunkMutation('POST', 15),
        }),
        submitImports: builder.mutation<
            ValidateImportsResponse[],
            {roomId: string; cabinets: CabinetSpecs[]}
        >({
            queryFn: createSequentialChunkMutation('PUT', 10, true),
            invalidatesTags: [
                'Job',
                'JobCost',
                'ProductRoom',
                'Cabinets',
                'TotalProductCount',
            ],
        }),
    }),
});

export const {useValidateImportsMutation, useSubmitImportsMutation} =
    ProductUploadApi;
